import soloIllustration from '@/assets/images/Transition_IllustrationSOLO6.png';
import teamIllustration from '@/assets/images/Transition_IllustrationTEAM6.png';
import workUnitsIllustration from '@/assets/images/Transition_Work_Units.png';
import {
    BUSTLY_DOWNLOAD_URL,
    useBustlyDesktopOpen,
} from '@/hooks/useBustlyDesktopOpen';
import { type Translate, useTranslation } from '@client-app/i18n';
import { ArrowUpRight } from '@phosphor-icons/react';
import { Check, X } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import styles from './PaymentSuccessModal.module.scss';
import { isSoloPlanCode, isTeamPlanCode } from './teamOfferSelection';

export type PaymentSuccessKind = 'subscription' | 'upgrade' | 'credits';

interface PaymentSuccessModalProps {
    isOpen: boolean;
    kind?: PaymentSuccessKind;
    planCode?: string | null;
    planName?: string | null;
    onClose: () => void;
}

interface PaymentSuccessContent {
    title: string;
    summary?: string;
    descriptionLines: string[];
    illustration: string;
    usesChecklist: boolean;
}

const normalizePlanLabel = (
    planCode?: string | null,
    planName?: string | null,
): string | null => {
    const normalizedPlanCode = (planCode || '').trim().toUpperCase();
    if (isTeamPlanCode(normalizedPlanCode) || normalizedPlanCode === 'TEAM') {
        return 'Team';
    }
    if (isSoloPlanCode(normalizedPlanCode) || normalizedPlanCode === 'SOLO') {
        return 'Solo';
    }

    return planName?.trim() || null;
};

export const getPaymentSuccessContent = ({
    kind = 'subscription',
    planCode,
    planName,
    t,
}: {
    kind?: PaymentSuccessKind;
    planCode?: string | null;
    planName?: string | null;
    t: Translate;
}): PaymentSuccessContent => {
    const title = t('website.paymentSuccess.title');
    if (kind === 'credits') {
        return {
            title,
            descriptionLines: [t('website.paymentSuccess.creditsDescription')],
            illustration: workUnitsIllustration,
            usesChecklist: false,
        };
    }

    const planLabel = normalizePlanLabel(planCode, planName);
    if (!planLabel) {
        return {
            title,
            summary: t('website.paymentSuccess.subscriptionSummary'),
            descriptionLines: [
                t('website.paymentSuccess.subscriptionLineOpenDesktop'),
                t('website.paymentSuccess.subscriptionLineUsePlan'),
                t('website.paymentSuccess.subscriptionLineManageBilling'),
            ],
            illustration: soloIllustration,
            usesChecklist: true,
        };
    }

    const isTeamPlan = planLabel === 'Team';
    return {
        title,
        summary: t('website.paymentSuccess.subscriptionSummaryWithPlan', { plan: planLabel }),
        descriptionLines: isTeamPlan
            ? [
                t('website.paymentSuccess.teamLineInvite'),
                t('website.paymentSuccess.teamLineConnect'),
                t('website.paymentSuccess.teamLineManageRoles'),
            ]
            : [
                t('website.paymentSuccess.soloLineOpenDesktop'),
                t('website.paymentSuccess.soloLineUseCredits'),
                t('website.paymentSuccess.soloLineBuyCredits'),
            ],
        illustration: isTeamPlan ? teamIllustration : soloIllustration,
        usesChecklist: true,
    };
};

export const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
    isOpen,
    kind = 'subscription',
    planCode,
    planName,
    onClose,
}) => {
    const { t } = useTranslation();
    const { handleOpenBustlyClick } = useBustlyDesktopOpen({
        onManualComplete: onClose,
    });

    if (!isOpen) {
        return null;
    }

    const content = getPaymentSuccessContent({
        kind, planCode, planName, t,
    });

    return createPortal(
        <div className={styles.overlay} onClick={onClose}>
            <section
                className={styles.modal}
                aria-modal="true"
                role="dialog"
                aria-labelledby="payment-success-title"
                aria-describedby="payment-success-description"
                onClick={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label={t('website.paymentSuccess.close')}
                >
                    <X size={18} strokeWidth={1.8} />
                </button>

                <div className={styles.copy}>
                    <h2 id="payment-success-title" className={styles.title}>
                        {content.title}
                    </h2>
                    {content.summary ? (
                        <p className={styles.description}>
                            {content.summary}
                        </p>
                    ) : null}
                    {content.usesChecklist ? (
                        <ul id="payment-success-description" className={styles.benefitList}>
                            {content.descriptionLines.map((line) => (
                                <li key={line} className={styles.benefitItem}>
                                    <span className={styles.checkIcon} aria-hidden="true">
                                        <Check size={13} strokeWidth={2} />
                                    </span>
                                    <span>{line}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p id="payment-success-description" className={styles.description}>
                            {content.descriptionLines[0]}
                        </p>
                    )}
                </div>

                <div className={styles.visualPanel} aria-hidden="true">
                    <img
                        src={content.illustration}
                        alt=""
                        className={styles.visualImage}
                    />
                </div>

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={() => {
                            void handleOpenBustlyClick();
                        }}
                    >
                        <span>{t('website.paymentSuccess.openDesktop')}</span>
                        <ArrowUpRight size={16} weight="regular" />
                    </button>
                    <div className={styles.footerNote}>
                        {t('website.paymentSuccess.downloadQuestion')}
                        {' '}
                        <a
                            href={BUSTLY_DOWNLOAD_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.downloadLink}
                        >
                            {t('website.paymentSuccess.downloadApp')}
                        </a>
                        .
                    </div>
                </div>
            </section>
        </div>,
        document.body,
    );
};
